/**
 * Neyra — user-created entities (services, events) + social bits.
 * Pure localStorage. No backend.
 */
import { MOCK_EVENTS, MOCK_SERVICES } from "@/lib/mockData";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const K = {
    SERVICES: "neara.services",
    EVENTS: "neara.events",
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

const genId = (prefix) =>
    prefix + "_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/* ---------------- Services ---------------- */

const asArray = (v) => (Array.isArray(v) ? v : []);

export const getUserServices = () => asArray(read(K.SERVICES, []));

export const getAllServices = () => [...getUserServices(), ...asArray(MOCK_SERVICES)];

/** Public listings — excludes services the owner has hidden. */
export const getPublicServices = () =>
    getAllServices().filter((s) => s && !s.hidden && !s.isHidden && !s.deleted);

export const getServiceById = (id) =>
    getAllServices().find((s) => s.id === id) || null;

export const createService = (user, payload) => {
    const now = new Date().toISOString();
    const pricingType = payload.pricingType || "from"; // "fixed" | "from" | "none"
    const priceNum = Number(payload.price) || 0;
    const lastGeo = getLastGeo();
    const lat = Number.isFinite(payload.lat) ? Number(payload.lat) : lastGeo?.lat ?? null;
    const lng = Number.isFinite(payload.lng) ? Number(payload.lng) : lastGeo?.lng ?? null;
    const service = {
        id: genId("s"),
        ownerId: user.id,
        name: payload.name.trim(),
        category: payload.category,
        categoryGroup: payload.categoryGroup || null,
        subcategory: payload.subcategory || null,
        customCategory: payload.customCategory || null,
        description: payload.description?.trim() || "",
        // Service type: in_person | online | hybrid
        serviceType: payload.serviceType || "in_person",
        // New structured pricing
        pricingType,
        price: pricingType === "none" ? 0 : priceNum,
        // Back-compat: priceFrom mirrors price for "from" / "fixed"; 0 for "none"
        priceFrom: pricingType === "none" ? 0 : priceNum,
        currency: "₦",
        location: payload.location?.trim() || "Lagos, Nigeria",
        lat,
        lng,
        // Optional availability { days: string[], from: "HH:mm", to: "HH:mm" }
        availability: payload.availability || null,
        // Contact toggles (effective only when provider is premium)
        whatsappEnabled: !!payload.whatsappEnabled,
        phoneEnabled: !!payload.phoneEnabled,
        rating: 0,
        reviews: 0,
        verified: false,
        premium: !!user.premium,
        image: payload.images?.[0] || "",
        images: payload.images || [],
        tags: [payload.category?.toLowerCase().replace(/\s+/g, "-")].filter(Boolean),
        providerName: user.fullName,
        providerUsername: user.username,
        providerAvatar: user.avatar || null,
        providerPhone: user.phone || null,
        createdAt: now,
    };
    const list = getUserServices();
    list.unshift(service);
    write(K.SERVICES, list);
    fireMirror(SUPABASE_TABLES.SERVICES, service);
    return service;
};

export const updateService = (id, patch) => {
    const list = getUserServices();
    const idx = list.findIndex((s) => s.id === id);
    if (idx < 0) return null;
    list[idx] = { ...list[idx], ...patch };
    write(K.SERVICES, list);
    fireMirror(SUPABASE_TABLES.SERVICES, list[idx]);
    return list[idx];
};

export const deleteService = (id) => {
    write(K.SERVICES, getUserServices().filter((s) => s.id !== id));
    fireMirror(SUPABASE_TABLES.SERVICES, { id, deleted: true, deletedAt: new Date().toISOString() });
};

export const updateEvent = (id, patch) => {
    const list = getUserEvents();
    const idx = list.findIndex((e) => e.id === id);
    if (idx < 0) return null;
    list[idx] = { ...list[idx], ...patch };
    write(K.EVENTS, list);
    fireMirror(SUPABASE_TABLES.EVENTS, list[idx]);
    return list[idx];
};

export const deleteEvent = (id) => {
    write(K.EVENTS, getUserEvents().filter((e) => e.id !== id));
    fireMirror(SUPABASE_TABLES.EVENTS, { id, deleted: true, deletedAt: new Date().toISOString() });
};

/** True when an event's date has fully passed (relative to now). */
export const isEventPast = (event) => {
    if (!event) return false;
    const raw = event.rawDate || "";
    const time = event.rawTime || "23:59";
    const ts = new Date(`${raw}T${time}`).getTime();
    if (Number.isNaN(ts)) return false;
    return ts < Date.now();
};

/**
 * Returns deterministic "attendees" count for an event (mocked).
 * Capacity = event.capacity (optional). Joining bumps the count by 1.
 */
export const getEventAttendees = (event, joined = false) => {
    if (!event) return { count: 0, capacity: null };
    const baseSeed = String(event.id || "");
    let h = 0;
    for (let i = 0; i < baseSeed.length; i++) h = (h * 31 + baseSeed.charCodeAt(i)) >>> 0;
    const baseCount = (h % 18) + 3; // 3..20 mocked attendees
    const count = baseCount + (joined ? 1 : 0);
    const capacity = event.capacity ? Number(event.capacity) : null;
    return { count, capacity };
};

export const formatServicePrice = (s) => {
    const c = s.currency || "₦";
    const t = s.pricingType || (s.priceFrom ? "from" : "none");
    const amt = Number(s.price ?? s.priceFrom ?? 0);
    if (t === "fixed") return `${c}${amt.toLocaleString()}`;
    if (t === "from")  return amt > 0 ? `From ${c}${amt.toLocaleString()}` : "Price on request";
    return "Price on request";
};

export const listServicesByOwner = (userId) =>
    getUserServices().filter((s) => s.ownerId === userId);

/* ---------------- Events ---------------- */

export const getUserEvents = () => asArray(read(K.EVENTS, []));

export const getAllEvents = () => [...getUserEvents(), ...asArray(MOCK_EVENTS)];

/** Public events — excludes events the host has hidden. */
export const getPublicEvents = () =>
    getAllEvents().filter((e) => e && !e.hidden && !e.isHidden && !e.deleted);

export const getEventById = (id) =>
    getAllEvents().find((e) => e.id === id) || null;

export const createEvent = (user, payload) => {
    const now = new Date().toISOString();
    const dateStr = formatEventDate(payload.date, payload.time);
    const price = payload.isFree ? 0 : Number(payload.price) || 0;
    const lastGeo = getLastGeo();
    const lat = Number.isFinite(payload.lat) ? Number(payload.lat) : lastGeo?.lat ?? null;
    const lng = Number.isFinite(payload.lng) ? Number(payload.lng) : lastGeo?.lng ?? null;
    const event = {
        id: genId("e"),
        ownerId: user.id,
        title: payload.title.trim(),
        category: payload.category,
        categoryGroup: payload.categoryGroup || null,
        subcategory: payload.subcategory || null,
        customCategory: payload.customCategory || null,
        date: dateStr,
        rawDate: payload.date,
        rawTime: payload.time,
        location: payload.location?.trim() || "Lagos, Nigeria",
        lat,
        lng,
        description: payload.description?.trim() || "",
        price,
        refundPolicy: payload.refundPolicy || null,
        currency: "₦",
        image: payload.images?.[0] || "",
        images: payload.images || [],
        tags: [payload.category?.toLowerCase().replace(/\s+/g, "-")].filter(Boolean),
        hostName: user.fullName,
        hostUsername: user.username,
        hostAvatar: user.avatar || null,
        createdAt: now,
    };
    const list = getUserEvents();
    list.unshift(event);
    write(K.EVENTS, list);
    fireMirror(SUPABASE_TABLES.EVENTS, event);
    return event;
};

export const listEventsByOwner = (userId) =>
    getUserEvents().filter((e) => e.ownerId === userId);

/* ---------------- Utilities ---------------- */

export const formatEventDate = (dateISO, timeStr) => {
    try {
        const d = new Date(dateISO + (timeStr ? `T${timeStr}` : "T00:00"));
        if (Number.isNaN(d.getTime())) return dateISO;
        const day = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
        const time = timeStr
            ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
            : "";
        return time ? `${day} · ${time}` : day;
    } catch {
        return dateISO;
    }
};

export const formatPrice = (n, c = "₦") =>
    n === 0 || !n ? "Free" : `${c}${Number(n).toLocaleString()}`;
